
import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, map, mergeMap, switchMap, take, tap, withLatestFrom } from 'rxjs/operators';
import { RiskOwnerEditComponent } from 'src/app/modules/home/risks/risk-owners/risk-owner-edit/risk-owner-edit.component';
import { RiskOwnerSelectionComponent } from 'src/app/modules/shared/risk-owner-selection/risk-owner-selection.component';
import { AlertService } from '../../commons/services/alert.service';
import { LaravelRiskOwnerService } from '../../commons/services/backend/laravel-risk-owner.service';
import * as RiskOwnerActions from '../actions/risk-owner.actions';
import { AppState } from '../reducers';
import { getRiskOwnerDialogId, getRiskOwnersTableState, getSelectionDialogId } from '../selectors/risk-owner.selectors';
import { TranslateService } from '@ngx-translate/core';


@Injectable()
export class RiskOwnerEffects {

  error$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RiskOwnerActions.saveRiskOwnerFailed, RiskOwnerActions.deleteRiskOwnerFailed),
      tap(({ error }) => {
        if (error) {
          this.alertService.showErrorMessage(`${this.translateService.instant('shared.errore')}`, error);
        }
      })
    ), { dispatch: false }
  );

  loadRiskOwners$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RiskOwnerActions.loadRiskOwners),
      switchMap(({ page, perPage, order, direction, filters, includes }) => {
        return this.riskOwnerService.list(page, perPage, order, direction, filters, includes)
          .pipe(
            map(result =>
              RiskOwnerActions.loadRiskOwnersCompleted({ riskOwners: result.data, currentPage: page, total: result.total, perPage, order, direction, filters, includes })
            ),
            catchError(error => {
              return of(RiskOwnerActions.loadRiskOwnersFailed({ error }))
            })
          )
      })
    )
  );

  changePage = createEffect(() =>
    this.actions$.pipe(
      ofType(RiskOwnerActions.changePage),
      withLatestFrom(this.store$.select(getRiskOwnersTableState)),
      map(([{ page, size }, { total, currentPage, perPage, direction, order, filters, includes }]) => RiskOwnerActions.loadRiskOwners({ page: page, perPage: size, order, direction, filters, includes }))
    )
  );

  changeSort = createEffect(() =>
    this.actions$.pipe(
      ofType(RiskOwnerActions.changeSort),
      withLatestFrom(this.store$.select(getRiskOwnersTableState)),
      map(([action, { total, currentPage, perPage, direction, order, filters, includes }]) => RiskOwnerActions.loadRiskOwners({ page: currentPage, perPage: perPage, order: action.order, direction: action.direction, filters, includes }))
    )
  );

  changeFilters = createEffect(() =>
    this.actions$.pipe(
      ofType(RiskOwnerActions.changeFilters),
      withLatestFrom(this.store$.select(getRiskOwnersTableState)),
      map(([{ filters }, { total, currentPage, perPage, direction, order, includes }]) => RiskOwnerActions.loadRiskOwners({ page: currentPage, perPage: perPage, order, direction, filters, includes }))
    )
  );

  editRiskOwner$ = createEffect(() => this.actions$.pipe(
    ofType(RiskOwnerActions.editRiskOwner),
    map(({ riskOwner }) => {
      let dialogRef = this.dialog.open(RiskOwnerEditComponent, {
        data: {
          riskOwner
        }
      });
      return RiskOwnerActions.riskOwnerDialogOpened({ dialogId: dialogRef.id });
    }))
  );

  saveRiskOwner$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RiskOwnerActions.saveRiskOwner),
      mergeMap(({ riskOwner }) =>
        this.riskOwnerService.upsert(riskOwner.toDTO())
          .pipe(
            map(result =>
              RiskOwnerActions.saveRiskOwnerCompleted({ riskOwner: result })
            ),
            catchError(error => of(RiskOwnerActions.saveRiskOwnerFailed({ error })))
          )
      )
    )
  );

  onSaveCompleted$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RiskOwnerActions.saveRiskOwnerCompleted),
      map(action => action.riskOwner),
      tap(riskOwner => this.alertService.showConfirmMessage(`${riskOwner.name} ${this.translateService.instant('shared.salvato con successo')}`)),
      map(() => RiskOwnerActions.closeRiskOwnerDialog())
    )
  );


  deleteRiskOwner$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RiskOwnerActions.deleteRiskOwner),
      switchMap(({ riskOwner }) =>
        this.alertService.showConfirmDialog(`${this.translateService.instant('shared.Conferma eliminazione')}`, `${this.translateService.instant('risks.Sei sicuro di voler eliminare il risk owner')} ${riskOwner.name}?`)
          .pipe(
            mergeMap((confirm) => {
              return confirm ?
                this.riskOwnerService.delete(riskOwner.id)
                  .pipe(
                    map(() => RiskOwnerActions.deleteRiskOwnerCompleted({ riskOwner })),
                    catchError(error => of(RiskOwnerActions.deleteRiskOwnerFailed({ error })))
                  )

                : of(RiskOwnerActions.deleteRiskOwnerCancelled());
            })
          )
      )
    )
  );

  onDeleteCompleted$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RiskOwnerActions.deleteRiskOwnerCompleted),
      tap(({ riskOwner }) => this.alertService.showConfirmMessage(` ${riskOwner.name} ${this.translateService.instant('shared.eliminato con successo')}`)),
      map(() => RiskOwnerActions.closeRiskOwnerDialog())
    )
  );

  closeDialog = createEffect(() =>
    this.actions$.pipe(
      ofType(RiskOwnerActions.closeRiskOwnerDialog),
      withLatestFrom(this.store$.select(getRiskOwnerDialogId)),
      tap(([_, dialogId]) => {
        if (dialogId) {
          this.dialog.getDialogById(dialogId).close();
        }
      })
    ), { dispatch: false }
  );

  reloadAfterSave = createEffect(() =>
    this.actions$.pipe(
      ofType(RiskOwnerActions.saveRiskOwnerCompleted),
      withLatestFrom(this.store$.select(getRiskOwnersTableState)),
      map(([_, { currentPage, perPage, direction, order, filters, includes }]) => RiskOwnerActions.loadRiskOwners({ page: currentPage, perPage, order, direction, filters, includes }))
    )
  );

  reloadAfterDelete = createEffect(() =>
    this.actions$.pipe(
      ofType(RiskOwnerActions.deleteRiskOwnerCompleted),
      withLatestFrom(this.store$.select(getRiskOwnersTableState)),
      map(([_, { currentPage, perPage, direction, order, filters, includes }]) => RiskOwnerActions.loadRiskOwners({ page: currentPage, perPage, order, direction, filters, includes }))
    )
  );

  selectRiskOwner$ = createEffect(() => this.actions$.pipe(
    ofType(RiskOwnerActions.selectRiskOwner),
    map(({ filters }) => {
      let dialogRef = this.dialog.open(RiskOwnerSelectionComponent, {
        data: {
          defaultFilters: filters
        }
      });
      return RiskOwnerActions.selectionDialogOpened({ selectionDialogId: dialogRef.id });
    }))
  );

  closeSelectionDialog = createEffect(() =>
    this.actions$.pipe(
      ofType(RiskOwnerActions.closeSelectionDialog),
      withLatestFrom(this.store$.select(getSelectionDialogId)),
      tap(([_, dialogId]) => {
        if (dialogId) {
          this.dialog.getDialogById(dialogId).close();
        }

      })
    ), { dispatch: false }
  );

  riskOwnersSelected$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RiskOwnerActions.riskOwnerSelected),
      map(() => RiskOwnerActions.closeSelectionDialog())
    ))


  constructor(
    private actions$: Actions,
    private store$: Store<AppState>,
    private riskOwnerService: LaravelRiskOwnerService,
    private dialog: MatDialog,
    private alertService: AlertService,
    private translateService: TranslateService
  ) { }
}
